// components/entities/tables/organisations/OrganisationsTable.tsx
'use client';

import { memo, useCallback, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useKernel } from '@/kernel/Kernel';
import { EntityView } from '@/components/entities/EntityView';
import type { InventoryEntityState, LeasingOrganisationDTO } from '#types/types';
import DeleteOrganisationModal from '@/components/DeleteOrganisationModal';
import { Grid } from '@/components/portal/Grid';

// ChatGPT's list page (owner, 2026-09-29: 'I don't even think the table is necessary'): the
// organisations as rows in one readable column, each opening its page, the rest behind "…".
export const OrganisationsTable = memo(({ onOpen }: { onOpen?: (organisationId: string) => void }) => {
  const { kernel } = useKernel();
  const [orgIdToDelete, setOrgIdToDelete] = useState<string | null>(null);

  const handleDeleteClick = useCallback((org: LeasingOrganisationDTO) => setOrgIdToDelete(org.id), []);
  const handleCloseDeleteModal = useCallback(() => setOrgIdToDelete(null), []);
  const handleConfirmDelete = useCallback(async (orgId: string, options: object) => {
    try {
      await axios.post('/api/admin', { action: 'deleteOrganisation', organisationId: orgId, ...options });
      toast.success('Organisation deleted');
      setOrgIdToDelete(null);
      await kernel.current?.refreshInventory('Organisation', undefined, undefined, { force: true });
    } catch (err: any) {
      throw new Error(err.response?.data?.message || err.message || 'Deletion failed');
    }
  }, [kernel]);

  const renderRow = useCallback((org: InventoryEntityState<'Organisation'>) => (
    <EntityView key={org.id} type="Organisation" entityId={org.id} variant="row" onDelete={handleDeleteClick} onClick={onOpen ? () => onOpen(org.id) : undefined} />
  ), [handleDeleteClick, onOpen]);

  return (
    <>
      <Grid
        entityType="Organisation"
        pageTitle="Organisations"
        searchPlaceholder="Search organisations"
        sortOptions={[]}
        pageSize={40}
        renderCard={renderRow}
        // The rows' hover pad hangs 12px outside the column so the avatars sit on the title's edge.
        gridClass="-mx-3 max-w-[944px] grid-cols-1 !gap-0.5"
      />
      {orgIdToDelete && (
        <DeleteOrganisationModal isOpen onClose={handleCloseDeleteModal} organisationId={orgIdToDelete} onSubmit={handleConfirmDelete} />
      )}
    </>
  );
});

OrganisationsTable.displayName = 'OrganisationsTable';
